import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { JSONContent } from "@tiptap/react";
import { useDropzone } from "react-dropzone";
import { FiEye, FiUploadCloud } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { Article } from "../../../apiServices/articles.service";
import { Categorie } from "../../../apiServices/categories.service";
import Editor from "../../../components/ui/Editor";
import { ARTICLE_TYPES, STATUSES } from "../constants/enums";
import { useAllCategories } from "../hooks/useAllCategories";
import { useFeedback } from "../hooks/FeedbackContext";
import {
  ArticleLangContent,
  findTranslation,
  langContentFromApi,
  toTranslationPayload,
} from "../helpers/articleLang";
import AdminArticlePreview from "./AdminArticlePreview";
import AdminArticleTranslationsDialog from "./AdminArticleTranslationsDialog";

export interface ArticleFormData {
  title: string;
  category: string;
  type: string;
  status: string;
  resumen: string;
  content: JSONContent;
  existingImageUrl?: string;
  translations?: any[];
}

interface AdminArticleFormProps {
  initialData: ArticleFormData;
  rawArticle?: Article | null;
  onSubmit: (
    data: ArticleFormData,
    imageFile: File | null,
    translationsPayload?: any[],
  ) => Promise<void>;
  loading: boolean;
  isEdit?: boolean;
}

export default function AdminArticleForm({
  initialData,
  rawArticle,
  onSubmit,
  loading,
  isEdit = false,
}: AdminArticleFormProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ArticleFormData>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [image, setImage] = useState<File | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const { categories } = useAllCategories();
  const { showFeedback } = useFeedback();

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const storedTranslations = useMemo(
    () => ({
      en: langContentFromApi(findTranslation(initialData.translations, "en")),
      pt: langContentFromApi(findTranslation(initialData.translations, "pt")),
    }),
    [initialData.translations],
  );

  const spanishDraft: ArticleLangContent = {
    title: formData.title,
    resumen: formData.resumen,
    content: formData.content,
  };

  const selectedCategoryName =
    categories?.find(
      (cat) => String(cat.categoriaId) === String(formData.category),
    )?.nombreEspanol ?? "";

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setImage(acceptedFiles[0]);
        if (errors.image) {
          setErrors((prev) => ({ ...prev, image: "" }));
        }
      }
    },
    [errors.image],
  );

  const onDropRejected = useCallback(() => {
    setErrors((prev) => ({
      ...prev,
      image: "El archivo es inválido o supera los 5MB",
    }));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/webp": [],
    },
    maxFiles: 1,
    maxSize: 5242880,
  });

  const handleChange = <K extends keyof ArticleFormData>(
    field: K,
    value: ArticleFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "El título en Español es requerido";
    }
    if (!formData.category) {
      newErrors.category = "Selecciona una categoría";
    }
    if (!formData.type) {
      newErrors.type = "Selecciona un tipo";
    }
    if (!formData.status) {
      newErrors.status = "Selecciona un estado";
    }

    if (!formData.resumen.trim()) {
      newErrors.resumen = "El resumen en Español es requerido";
    } else if (formData.resumen.split("\n").length > 5) {
      newErrors.resumen = "El resumen no puede superar los 5 renglones";
    }

    const checkHasActualContent = (node: JSONContent): boolean => {
      if (node.text && node.text.trim().length > 0) return true;
      if (node.type === "image") return true;
      if (node.content && node.content.length > 0) {
        return node.content.some((childNode) =>
          checkHasActualContent(childNode),
        );
      }
      return false;
    };

    if (!checkHasActualContent(formData.content)) {
      newErrors.content = "El contenido en Español es requerido";
    }

    if (!image && !formData.existingImageUrl) {
      newErrors.image = "La imagen de portada es requerida";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setReviewOpen(true);
  };

  const handleConfirmReview = async (drafts: Record<"es" | "en" | "pt", ArticleLangContent>) => {
    try {
      const translationsPayload = (["es", "en", "pt"] as const).map((lang) =>
        toTranslationPayload(lang, drafts[lang]),
      );

      await onSubmit(
        {
          ...formData,
          title: drafts.es.title,
          resumen: drafts.es.resumen,
          content: drafts.es.content,
        },
        image,
        translationsPayload,
      );
      showFeedback(
        isEdit
          ? "Artículo actualizado correctamente"
          : "Artículo creado correctamente",
        "success",
      );
      setReviewOpen(false);
    } catch (error: any) {
      showFeedback(
        error?.message && error.message !== "Network Error"
          ? error.message
          : isEdit
            ? "Ocurrió un error al actualizar el artículo"
            : "Ocurrió un error al crear el artículo",
        "error",
      );
    }
  };

  const [imagePreview, setImagePreview] = useState<string | undefined>(
    formData.existingImageUrl,
  );

  useEffect(() => {
    if (!image) {
      setImagePreview(formData.existingImageUrl);
      return;
    }
    const objectUrl = URL.createObjectURL(image);
    setImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image, formData.existingImageUrl]);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1000,
        mx: "auto",
        px: { xs: 1, sm: 0 },
        animation: "fadeIn 0.3s ease-in-out",
      }}
    >
      {(rawArticle?.fechaActualizacion || rawArticle?.fechaCreacion) && (
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
          {`Ult. actualización: ${(() => {
            const dateStr =
              rawArticle.fechaActualizacion || rawArticle.fechaCreacion!;
            const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
            const dateObj = match
              ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
              : new Date(dateStr);
            return dateObj.toLocaleDateString("es-AR");
          })()} (${
            rawArticle.usuarioActualizacion ||
            rawArticle.usuarioCreacion ||
            "Usuario desconocido"
          })`}
        </Typography>
      )}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {isEdit ? "Editar Artículo" : "Nueva Publicación"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Escribí el artículo en español. Al guardar vas a revisar las versiones
          en inglés y portugués antes de confirmar.
        </Typography>
      </Box>

      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          p: { xs: 2, sm: 4 },
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <TextField
            label="Título del Artículo"
            value={formData.title}
            onChange={(e) => handleChange("title", e.target.value)}
            error={!!errors.title}
            helperText={errors.title}
            disabled={loading}
            fullWidth
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
            }}
          >
            <FormControl fullWidth error={!!errors.category} disabled={loading}>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={formData.category}
                label="Categoría"
                onChange={(e) => handleChange("category", e.target.value)}
              >
                {categories?.map((cat: Categorie) => (
                  <MenuItem key={cat.categoriaId} value={cat.categoriaId}>
                    {cat.nombreEspanol}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && (
                <FormHelperText>{errors.category}</FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth error={!!errors.type} disabled={loading}>
              <InputLabel>Tipo de Artículo</InputLabel>
              <Select
                value={formData.type}
                label="Tipo de Artículo"
                onChange={(e) => handleChange("type", e.target.value)}
              >
                {ARTICLE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth error={!!errors.status} disabled={loading}>
              <InputLabel>Estado</InputLabel>
              <Select
                value={formData.status}
                label="Estado"
                onChange={(e) => handleChange("status", e.target.value)}
              >
                {STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.status && (
                <FormHelperText>{errors.status}</FormHelperText>
              )}
            </FormControl>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                color: errors.image ? "error.main" : "text.primary",
              }}
            >
              Imagen de Portada
            </Typography>

            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed",
                borderColor: errors.image
                  ? "error.main"
                  : isDragActive
                    ? "primary.main"
                    : "divider",
                borderRadius: "8px",
                p: { xs: 2, sm: 4 },
                textAlign: "center",
                cursor: loading ? "default" : "pointer",
                bgcolor: isDragActive ? "action.hover" : "background.paper",
                transition: "all 0.2s ease",
                position: "relative",
              }}
            >
              <input {...getInputProps()} disabled={loading} />

              {imagePreview ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{
                      width: "100%",
                      maxWidth: 300,
                      maxHeight: 200,
                      objectFit: "contain",
                      borderRadius: "4px",
                      mb: 1,
                    }}
                  />

                  {image && (
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: "primary.main",
                        wordBreak: "break-all",
                      }}
                    >
                      {image.name}
                    </Typography>
                  )}

                  {image && (
                    <Typography variant="caption" color="text.secondary">
                      {(image.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  )}

                  {!loading && (
                    <Button
                      size="small"
                      color="error"
                      sx={{
                        display: "block",
                        mx: "auto",
                        mt: 1,
                        textTransform: "none",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setImage(null);
                        setFormData((prev) => ({
                          ...prev,
                          existingImageUrl: undefined,
                        }));
                      }}
                    >
                      Eliminar imagen
                    </Button>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FiUploadCloud
                    size={32}
                    color="var(--color-primary, #0069a7)"
                  />
                  <Typography
                    variant="body1"
                    sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                  >
                    Arrastra una imagen o haz clic para seleccionar
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PNG, JPG, WEBP hasta 5MB
                  </Typography>
                </Box>
              )}
            </Box>

            {errors.image && (
              <FormHelperText error>{errors.image}</FormHelperText>
            )}
          </Box>

          <TextField
            label="Resumen del Artículo"
            value={formData.resumen}
            onChange={(e) => handleChange("resumen", e.target.value)}
            error={!!errors.resumen}
            helperText={
              errors.resumen ||
              "Resumen corto de la publicación (hasta 200 caracteres.)"
            }
            slotProps={{
              htmlInput: {
                maxLength: 200,
              },
            }}
            disabled={loading}
            multiline
            maxRows={5}
            fullWidth
          />

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                color: errors.content ? "error.main" : "text.primary",
              }}
            >
              Contenido del Artículo
            </Typography>
            <Box
              sx={{
                pointerEvents: loading ? "none" : "auto",
                opacity: loading ? 0.7 : 1,
                width: "100%",
              }}
            >
              <Editor
                content={formData.content as JSONContent}
                onChange={(json) => handleChange("content", json)}
              />
            </Box>
            {errors.content && (
              <FormHelperText error>{errors.content}</FormHelperText>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              justifyContent: "flex-end",
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/admin/articulos")}
              disabled={loading}
              fullWidth={{ xs: true, sm: false } as any}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                color: "text.primary",
                borderColor: "divider",
              }}
            >
              Cancelar
            </Button>

            <Button
              variant="outlined"
              onClick={() => setPreviewOpen(true)}
              disabled={loading}
              startIcon={<FiEye />}
              fullWidth={{ xs: true, sm: false } as any}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                color: "primary.main",
                borderColor: "primary.main",
                "&:hover": { bgcolor: "primary.50" },
              }}
            >
              Vista Previa
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth={{ xs: true, sm: false } as any}
              sx={{ textTransform: "none", borderRadius: "8px", minWidth: 140 }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : isEdit ? (
                "Guardar Cambios"
              ) : (
                "Guardar"
              )}
            </Button>
          </Box>
        </Box>
      </Paper>

      <AdminArticleTranslationsDialog
        open={reviewOpen}
        isEdit={isEdit}
        spanish={spanishDraft}
        storedTranslations={storedTranslations}
        categoryName={selectedCategoryName}
        saving={loading}
        onClose={() => setReviewOpen(false)}
        onConfirm={handleConfirmReview}
      />

      <AdminArticlePreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={formData.title}
        content={formData.content}
        imagePreview={imagePreview}
        categoryId={formData.category}
        status={formData.status}
        categories={categories ?? []}
        type={formData.type}
        resumen={formData.resumen}
      />
    </Box>
  );
}
